"use client";

import { c as _c } from "react/compiler-runtime";
import { FiInfo } from "react-icons/fi";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/Components/ui/tooltip";

// Read-only slider component for displaying metrics with single solid color
const ReadOnlySlider = t0 => {
  const $ = _c(5);
  const {
    value,
    type: t1
  } = t0;
  const type = t1 === undefined ? "confidence" : t1;
  const percentage = Math.min(Math.max(value || 0, 0), 100);
  let t2;
  if ($[0] !== type) {
    t2 = () => {
      if (type === "confidence") {
        return "#8b5cf6";
      } else {
        if (type === "accessibility") {
          return "#3b82f6";
        }
      }
      return "#6B7280";
    };
    $[0] = type;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getSliderColor = t2;
  const sliderColor = getSliderColor();
  const t3 = `${percentage}%`;
  let t4;
  if ($[2] !== sliderColor || $[3] !== t3) {
    t4 = <div className="w-full"><div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden"><div className="h-full rounded-full transition-all duration-500 ease-out" style={{
          width: t3,
          backgroundColor: sliderColor
        }} /></div></div>;
    $[2] = sliderColor;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};

// Helper function to get value color based on score (same logic as Accessibility.jsx)
const getValueColor = score => {
  if (score < 20) return '#9da2ae'; // Orange
  if (score < 50) return '#6A5ACD'; // Purple
  if (score < 75) return '#1E5EFF'; // Blue
  return '#00BE52'; // Green
};

// Helper function for inverse color (for Concerns - lower is better, max 5)
const getInverseValueColor = (score, maxValue = 5) => {
  // 0 = Green (best), maxValue = Orange (worst)
  if (score === 0) return '#9da2ae'; // Green - no concerns is best
  if (score === 1) return '#1E5EFF'; // Blue
  if (score <= 3) return '#6A5ACD'; // Purple
  return '#F59E0B'; // Orange - high concerns is worst
};

// Individual Metric Card Component
const MetricDisplayCard = t0 => {
  const $ = _c(37);
  const {
    icon: Icon,
    iconBgColor: t1,
    label,
    value,
    description,
    tooltipText,
    showSlider: t2,
    sliderType: t3,
    valueSuffix: t4,
    valueColor: t5,
    inverseColor: t6,
    maxValue: t7
  } = t0;
  const iconBgColor = t1 === undefined ? "bg-yellow-100" : t1;
  const showSlider = t2 === undefined ? true : t2;
  const sliderType = t3 === undefined ? "confidence" : t3;
  const valueSuffix = t4 === undefined ? "%" : t4;
  const valueColor = t5 === undefined ? null : t5;
  const inverseColor = t6 === undefined ? false : t6;
  const maxValue = t7 === undefined ? 100 : t7;
  const numericValue = typeof value === "number" ? value : 0;
  let t8;
  if ($[0] !== inverseColor || $[1] !== maxValue || $[2] !== numericValue || $[3] !== valueColor) {
    t8 = valueColor ? valueColor : inverseColor ? getInverseValueColor(numericValue, maxValue) : getValueColor(numericValue);
    $[0] = inverseColor;
    $[1] = maxValue;
    $[2] = numericValue;
    $[3] = valueColor;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const displayColor = t8;
  let t9;
  if ($[5] !== Icon || $[6] !== iconBgColor) {
    t9 = Icon && <div className={`w-5 sm:w-6 h-5 sm:h-6 rounded-md ${iconBgColor} flex items-center justify-center`}><Icon className="w-3 sm:w-3.5 h-3 sm:h-3.5" /></div>;
    $[5] = Icon;
    $[6] = iconBgColor;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== label) {
    t10 = <span className="text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wide font-poppins">{label}</span>;
    $[8] = label;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t9) {
    t11 = <div className="flex items-center gap-1.5 sm:gap-2">{t9}{t10}</div>;
    $[10] = t10;
    $[11] = t9;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== tooltipText) {
    t12 = tooltipText && <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><span className="cursor-help"><FiInfo className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-400" /></span></TooltipTrigger><TooltipContent><p className="text-xs max-w-[250px]">{tooltipText}</p></TooltipContent></Tooltip></TooltipProvider>;
    $[13] = tooltipText;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t11 || $[16] !== t12) {
    t13 = <div className="flex items-center justify-between mb-2 sm:mb-3">{t11}{t12}</div>;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== displayColor) {
    t14 = {
      color: displayColor
    };
    $[18] = displayColor;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== value) {
    t15 = typeof value === "number" ? Number.isInteger(value) ? value : value.toFixed(1) : value;
    $[20] = value;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t14 || $[23] !== t15 || $[24] !== valueSuffix) {
    t16 = <div className="text-2xl sm:text-3xl font-bold font-poppins mb-2 sm:mb-3" style={t14}>{t15}{valueSuffix}</div>;
    $[22] = t14;
    $[23] = t15;
    $[24] = valueSuffix;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== showSlider || $[27] !== sliderType || $[28] !== value) {
    t17 = showSlider && <div className="mb-2 sm:mb-3"><ReadOnlySlider value={value} type={sliderType} /></div>;
    $[26] = showSlider;
    $[27] = sliderType;
    $[28] = value;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== description) {
    t18 = <p className="text-[10px] sm:text-xs text-gray-400 font-poppins leading-tight sm:leading-normal">{description}</p>;
    $[30] = description;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t13 || $[33] !== t16 || $[34] !== t17 || $[35] !== t18) {
    t19 = <div className="bg-white rounded-lg sm:rounded-xl border border-gray-100 p-3 sm:p-4 flex flex-col min-w-0 sm:min-w-[280px] flex-1">{t13}{t16}{t17}{t18}</div>;
    $[32] = t13;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
};
export { MetricDisplayCard, ReadOnlySlider };
export default MetricDisplayCard;